// ─────────────────────────────────────────
//  CONSTANTS
// ─────────────────────────────────────────

export const PIECES = {
  wP:"♙",wR:"♖",wN:"♘",wB:"♗",wQ:"♕",wK:"♔",
  bP:"♟",bR:"♜",bN:"♞",bB:"♝",bQ:"♛",bK:"♚"
};

// ─────────────────────────────────────────
//  BOARD INIT & CLONE
// ─────────────────────────────────────────

export function getInitialBoard() {
  return {
    white: { pawns:[48,49,50,51,52,53,54,55], rooks:[56,63], knights:[57,62], bishops:[58,61], queens:[59], king:[60] },
    black: { pawns:[8,9,10,11,12,13,14,15],   rooks:[0,7],  knights:[1,6],   bishops:[2,5],   queens:[3],  king:[4]  },
    flags: { wKm:false, bKm:false, wLRm:false, wRRm:false, bLRm:false, bRRm:false },
    ep: null,
  };
}

export function cloneBoard(b) {
  return {
    white: { pawns:[...b.white.pawns], rooks:[...b.white.rooks], knights:[...b.white.knights], bishops:[...b.white.bishops], queens:[...b.white.queens], king:[...b.white.king] },
    black: { pawns:[...b.black.pawns], rooks:[...b.black.rooks], knights:[...b.black.knights], bishops:[...b.black.bishops], queens:[...b.black.queens], king:[...b.black.king] },
    flags: { ...b.flags },
    ep: b.ep,
  };
}

export function getInitialGame(mode = "pvp") {
  const board = getInitialBoard();
  return {
    board,
    turn: "white",
    status: "white to move",
    selected: null,
    selectedType: null,
    legalMoves: [],
    promo: null,
    history: [],
    capW: [],
    capB: [],
    halfmove: 0,
    posHist: { [posKey(board, "white")]: 1 },
    drawOffer: null,
    flipped: false,
    mode,
    difficulty: "novice",
    botThinking: false,
    lastMove: null,
  };
}

// ─────────────────────────────────────────
//  PIECE HELPERS
// ─────────────────────────────────────────

export function isWP(sq, b) {
  return b.white.pawns.includes(sq) || b.white.rooks.includes(sq) || b.white.knights.includes(sq) ||
         b.white.bishops.includes(sq) || b.white.queens.includes(sq) || b.white.king.includes(sq);
}
export function isBP(sq, b) {
  return b.black.pawns.includes(sq) || b.black.rooks.includes(sq) || b.black.knights.includes(sq) ||
         b.black.bishops.includes(sq) || b.black.queens.includes(sq) || b.black.king.includes(sq);
}
export function isOcc(sq, b) { return isWP(sq, b) || isBP(sq, b); }

export function getPieceType(sq, color, b) {
  const p = b[color];
  if (p.pawns.includes(sq))   return "pawn";
  if (p.rooks.includes(sq))   return "rook";
  if (p.knights.includes(sq)) return "knight";
  if (p.bishops.includes(sq)) return "bishop";
  if (p.queens.includes(sq))  return "queen";
  if (p.king.includes(sq))    return "king";
  return null;
}

export function getPieceAt(sq, b) {
  const w = b.white, bl = b.black;
  if (w.pawns.includes(sq))   return PIECES.wP;
  if (w.rooks.includes(sq))   return PIECES.wR;
  if (w.knights.includes(sq)) return PIECES.wN;
  if (w.bishops.includes(sq)) return PIECES.wB;
  if (w.queens.includes(sq))  return PIECES.wQ;
  if (w.king.includes(sq))    return PIECES.wK;
  if (bl.pawns.includes(sq))  return PIECES.bP;
  if (bl.rooks.includes(sq))  return PIECES.bR;
  if (bl.knights.includes(sq))return PIECES.bN;
  if (bl.bishops.includes(sq))return PIECES.bB;
  if (bl.queens.includes(sq)) return PIECES.bQ;
  if (bl.king.includes(sq))   return PIECES.bK;
  return "";
}

export function sqToAlg(sq) {
  return String.fromCharCode(97 + sq % 8) + (8 - Math.floor(sq / 8));
}

// ─────────────────────────────────────────
//  MOVE GENERATION
// ─────────────────────────────────────────

export function pawnAttacks(sq, color) {
  const attacks = [], c = sq % 8;
  if (color === "white") {
    if (c > 0 && sq - 9 >= 0) attacks.push(sq - 9);
    if (c < 7 && sq - 7 >= 0) attacks.push(sq - 7);
  } else {
    if (c > 0 && sq + 7 < 64) attacks.push(sq + 7);
    if (c < 7 && sq + 9 < 64) attacks.push(sq + 9);
  }
  return attacks;
}

export function sliding(sq, color, dirs, b) {
  const moves = [];
  for (const [dr, dc] of dirs) {
    let r = Math.floor(sq / 8), c = sq % 8;
    r += dr; c += dc;
    while (r >= 0 && r < 8 && c >= 0 && c < 8) {
      const ns = r * 8 + c;
      if (!isOcc(ns, b)) { moves.push(ns); }
      else {
        if (color === "white" && isBP(ns, b)) moves.push(ns);
        if (color === "black" && isWP(ns, b)) moves.push(ns);
        break;
      }
      r += dr; c += dc;
    }
  }
  return moves;
}

export function pawnMoves(sq, color, b) {
  const moves = [], r = Math.floor(sq / 8), c = sq % 8;
  if (color === "white") {
    if (sq - 8 >= 0 && !isOcc(sq - 8, b)) {
      moves.push(sq - 8);
      if (r === 6 && !isOcc(sq - 16, b)) moves.push(sq - 16);
    }
    if (c > 0 && sq - 9 >= 0 && isBP(sq - 9, b)) moves.push(sq - 9);
    if (c < 7 && sq - 7 >= 0 && isBP(sq - 7, b)) moves.push(sq - 7);
    if (b.ep !== null) {
      const er = Math.floor(b.ep / 8), ec = b.ep % 8;
      if (r === 3 && er === 2 && Math.abs(ec - c) === 1) moves.push(b.ep);
    }
  } else {
    if (sq + 8 < 64 && !isOcc(sq + 8, b)) {
      moves.push(sq + 8);
      if (r === 1 && !isOcc(sq + 16, b)) moves.push(sq + 16);
    }
    if (c > 0 && sq + 7 < 64 && isWP(sq + 7, b)) moves.push(sq + 7);
    if (c < 7 && sq + 9 < 64 && isWP(sq + 9, b)) moves.push(sq + 9);
    if (b.ep !== null) {
      const er = Math.floor(b.ep / 8), ec = b.ep % 8;
      if (r === 4 && er === 5 && Math.abs(ec - c) === 1) moves.push(b.ep);
    }
  }
  return moves;
}

export function rookMoves(sq, color, b)   { return sliding(sq, color, [[-1,0],[1,0],[0,-1],[0,1]], b); }
export function bishopMoves(sq, color, b) { return sliding(sq, color, [[-1,-1],[-1,1],[1,-1],[1,1]], b); }
export function queenMoves(sq, color, b)  { return sliding(sq, color, [[-1,0],[1,0],[0,-1],[0,1],[-1,-1],[-1,1],[1,-1],[1,1]], b); }

export function knightMoves(sq, color, b) {
  const moves = [], r = Math.floor(sq / 8), c = sq % 8;
  for (const [dr, dc] of [[-2,-1],[-2,1],[-1,-2],[-1,2],[1,-2],[1,2],[2,-1],[2,1]]) {
    const nr = r + dr, nc = c + dc;
    if (nr >= 0 && nr < 8 && nc >= 0 && nc < 8) {
      const ns = nr * 8 + nc;
      if (color === "white" ? !isWP(ns, b) : !isBP(ns, b)) moves.push(ns);
    }
  }
  return moves;
}

export function kingMoves(sq, color, b) {
  const moves = [], r = Math.floor(sq / 8), c = sq % 8;
  for (const [dr, dc] of [[-1,-1],[-1,0],[-1,1],[0,-1],[0,1],[1,-1],[1,0],[1,1]]) {
    const nr = r + dr, nc = c + dc;
    if (nr >= 0 && nr < 8 && nc >= 0 && nc < 8) {
      const ns = nr * 8 + nc;
      if (color === "white" ? !isWP(ns, b) : !isBP(ns, b)) moves.push(ns);
    }
  }
  return moves;
}

export function castleMoves(color, b) {
  const moves = [], enemy = color === "white" ? "black" : "white";
  if (color === "white") {
    if (b.white.king[0] !== 60 || b.flags.wKm || inCheck("white", b)) return moves;
    if (b.white.rooks.includes(63) && !b.flags.wRRm && !isOcc(61,b) && !isOcc(62,b) && !isAttacked(61,enemy,b) && !isAttacked(62,enemy,b)) moves.push(62);
    if (b.white.rooks.includes(56) && !b.flags.wLRm && !isOcc(59,b) && !isOcc(58,b) && !isOcc(57,b) && !isAttacked(59,enemy,b) && !isAttacked(58,enemy,b)) moves.push(58);
  } else {
    if (b.black.king[0] !== 4 || b.flags.bKm || inCheck("black", b)) return moves;
    if (b.black.rooks.includes(7) && !b.flags.bRRm && !isOcc(5,b) && !isOcc(6,b) && !isAttacked(5,enemy,b) && !isAttacked(6,enemy,b)) moves.push(6);
    if (b.black.rooks.includes(0) && !b.flags.bLRm && !isOcc(3,b) && !isOcc(2,b) && !isOcc(1,b) && !isAttacked(3,enemy,b) && !isAttacked(2,enemy,b)) moves.push(2);
  }
  return moves;
}

export function pseudoMoves(sq, type, color, b) {
  if (type === "pawn")   return pawnMoves(sq, color, b);
  if (type === "rook")   return rookMoves(sq, color, b);
  if (type === "knight") return knightMoves(sq, color, b);
  if (type === "bishop") return bishopMoves(sq, color, b);
  if (type === "queen")  return queenMoves(sq, color, b);
  if (type === "king")   return [...kingMoves(sq, color, b), ...castleMoves(color, b)];
  return [];
}

export function isAttacked(sq, attacker, b) {
  const p = b[attacker];
  for (const ps of p.pawns)   if (pawnAttacks(ps, attacker).includes(sq)) return true;
  for (const rs of p.rooks)   if (rookMoves(rs, attacker, b).includes(sq)) return true;
  for (const ns of p.knights) if (knightMoves(ns, attacker, b).includes(sq)) return true;
  for (const bs of p.bishops) if (bishopMoves(bs, attacker, b).includes(sq)) return true;
  for (const qs of p.queens)  if (queenMoves(qs, attacker, b).includes(sq)) return true;
  for (const ks of p.king) {
    const kr = Math.floor(ks/8), kc = ks%8, tr = Math.floor(sq/8), tc = sq%8;
    if (Math.abs(kr-tr) <= 1 && Math.abs(kc-tc) <= 1) return true;
  }
  return false;
}

export function inCheck(color, b) {
  const ks = b[color].king[0];
  if (ks === undefined) return false;
  return isAttacked(ks, color === "white" ? "black" : "white", b);
}

export function getLegalMoves(sq, type, color, b) {
  return pseudoMoves(sq, type, color, b).filter(to => {
    const promo = (type === "pawn" && ((color === "white" && Math.floor(to/8) === 0) || (color === "black" && Math.floor(to/8) === 7))) ? "queen" : null;
    return !inCheck(color, applyMove(b, sq, type, color, to, promo).board);
  });
}

export function getAllLegalMoves(color, b) {
  const moves = [], p = b[color];
  for (const [type, squares] of [["pawn",p.pawns],["rook",p.rooks],["knight",p.knights],["bishop",p.bishops],["queen",p.queens],["king",p.king]]) {
    for (const sq of squares) {
      for (const to of getLegalMoves(sq, type, color, b)) moves.push({ from:sq, to, type });
    }
  }
  return moves;
}

export function isCheckmate(color, b) { return inCheck(color, b) && getAllLegalMoves(color, b).length === 0; }
export function isStalemate(color, b) { return !inCheck(color, b) && getAllLegalMoves(color, b).length === 0; }

export function insufficientMaterial(b) {
  const w = b.white, bl = b.black;
  const wc = w.queens.length+w.rooks.length+w.pawns.length+w.knights.length+w.bishops.length;
  const bc = bl.queens.length+bl.rooks.length+bl.pawns.length+bl.knights.length+bl.bishops.length;
  if (wc === 0 && bc === 0) return true;
  if (w.queens.length===0&&w.rooks.length===0&&w.pawns.length===0&&bl.queens.length===0&&bl.rooks.length===0&&bl.pawns.length===0) {
    const wm = w.knights.length+w.bishops.length, bm = bl.knights.length+bl.bishops.length;
    if (wm <= 1 && bm === 0) return true;
    if (wm === 0 && bm <= 1) return true;
    if (w.knights.length===0&&bl.knights.length===0&&w.bishops.length===1&&bl.bishops.length===1) return true;
  }
  return false;
}

// ─────────────────────────────────────────
//  APPLY MOVE
// ─────────────────────────────────────────

export function applyMove(b, from, type, color, to, promo = null) {
  let nb = cloneBoard(b);
  nb.ep = null;
  const enemy = color === "white" ? "black" : "white";

  const isEP = type === "pawn" && b.ep !== null && to === b.ep && !isOcc(to, b);

  if (to === 56) nb.flags.wLRm = true;
  if (to === 63) nb.flags.wRRm = true;
  if (to === 0)  nb.flags.bLRm = true;
  if (to === 7)  nb.flags.bRRm = true;

  let capturedType = null;

  if (isEP) {
    const cs = color === "white" ? to + 8 : to - 8;
    capturedType = "pawn";
    nb[enemy].pawns = nb[enemy].pawns.filter(s => s !== cs);
  } else {
    const ct = getPieceType(to, enemy, b);
    if (ct) {
      capturedType = ct;
      nb[enemy][ct + "s"] = nb[enemy][ct + "s"].filter(s => s !== to);
      if (ct === "king") nb[enemy].king = nb[enemy].king.filter(s => s !== to);
    }
  }

  const pKey = type === "king" ? "king" : type + "s";
  nb[color][pKey] = nb[color][pKey].filter(s => s !== from);

  const isPromoMove = type === "pawn" && ((color === "white" && Math.floor(to/8) === 0) || (color === "black" && Math.floor(to/8) === 7));

  if (isPromoMove) {
    const pp = promo || "queen";
    const ppKey = pp === "king" ? "king" : pp + "s";
    nb[color][ppKey] = [...nb[color][ppKey], to];
  } else {
    nb[color][pKey] = [...nb[color][pKey], to];
  }

  if (type === "pawn" && Math.abs(to - from) === 16)
    nb.ep = color === "white" ? from - 8 : from + 8;

  if (type === "king") {
    if (color === "white") {
      nb.flags.wKm = true;
      if (from === 60 && to === 62) { nb.white.rooks = nb.white.rooks.filter(s=>s!==63); nb.white.rooks = [...nb.white.rooks, 61]; nb.flags.wRRm = true; }
      if (from === 60 && to === 58) { nb.white.rooks = nb.white.rooks.filter(s=>s!==56); nb.white.rooks = [...nb.white.rooks, 59]; nb.flags.wLRm = true; }
    } else {
      nb.flags.bKm = true;
      if (from === 4 && to === 6) { nb.black.rooks = nb.black.rooks.filter(s=>s!==7); nb.black.rooks = [...nb.black.rooks, 5]; nb.flags.bRRm = true; }
      if (from === 4 && to === 2) { nb.black.rooks = nb.black.rooks.filter(s=>s!==0); nb.black.rooks = [...nb.black.rooks, 3]; nb.flags.bLRm = true; }
    }
  }

  if (type === "rook") {
    if (color === "white") { if (from===56) nb.flags.wLRm=true; if (from===63) nb.flags.wRRm=true; }
    else                   { if (from===0)  nb.flags.bLRm=true; if (from===7)  nb.flags.bRRm=true; }
  }

  return { board: nb, capturedType };
}

// ─────────────────────────────────────────
//  POSITION KEY & NOTATION
// ─────────────────────────────────────────

export function posKey(b, side) {
  const s = a => [...a].sort((x,y)=>x-y).join(",");
  const f = b.flags;
  const cr = [
    (!f.wKm&&!f.wRRm&&b.white.king.includes(60)&&b.white.rooks.includes(63))?"K":"",
    (!f.wKm&&!f.wLRm&&b.white.king.includes(60)&&b.white.rooks.includes(56))?"Q":"",
    (!f.bKm&&!f.bRRm&&b.black.king.includes(4) &&b.black.rooks.includes(7)) ?"k":"",
    (!f.bKm&&!f.bLRm&&b.black.king.includes(4) &&b.black.rooks.includes(0)) ?"q":"",
  ].join("") || "-";
  return [s(b.white.pawns),s(b.white.rooks),s(b.white.knights),s(b.white.bishops),s(b.white.queens),s(b.white.king),
          s(b.black.pawns),s(b.black.rooks),s(b.black.knights),s(b.black.bishops),s(b.black.queens),s(b.black.king),
          side, cr, b.ep===null?"-":b.ep].join("|");
}

export function buildNotation(type, from, to, captured, promo, nb, nextTurn, prevBoard) {
  let n = "";
  if (type === "king" && Math.abs(to - from) === 2) {
    n = to > from ? "O-O" : "O-O-O";
  } else {
    const pm = { pawn:"", rook:"R", knight:"N", bishop:"B", queen:"Q", king:"K" };
    n = pm[type];
    // SAN disambiguation: check if another piece of the same type can also reach `to`
    if (type !== "pawn" && type !== "king" && prevBoard) {
      const color = nextTurn === "black" ? "white" : "black";
      const siblings = (prevBoard[color][type + "s"] || []).filter(sq => sq !== from);
      const ambiguous = siblings.filter(sq => getLegalMoves(sq, type, color, prevBoard).includes(to));
      if (ambiguous.length > 0) {
        const ff = from % 8, fr = Math.floor(from / 8);
        const sameFile = ambiguous.some(sq => sq % 8 === ff);
        const sameRank = ambiguous.some(sq => Math.floor(sq / 8) === fr);
        if (!sameFile)            n += String.fromCharCode(97 + ff);
        else if (!sameRank)       n += String(8 - fr);
        else                      n += String.fromCharCode(97 + ff) + String(8 - fr);
      }
    }
    if (type === "pawn" && captured) n += String.fromCharCode(97 + from % 8);
    if (captured) n += "x";
    n += sqToAlg(to);
    if (promo) { const pl = { queen:"Q", rook:"R", bishop:"B", knight:"N" }; n += "=" + pl[promo]; }
  }
  if (isCheckmate(nextTurn, nb)) n += "#";
  else if (inCheck(nextTurn, nb)) n += "+";
  return n;
}

// ─────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────

export function isGameOver(status) {
  return status.includes("wins") || status === "stalemate" || status.startsWith("draw");
}

export function capSymbol(type, color) {
  const map = { pawn: color==="white"?"♙":"♟", rook: color==="white"?"♖":"♜", knight: color==="white"?"♘":"♞", bishop: color==="white"?"♗":"♝", queen: color==="white"?"♕":"♛" };
  return map[type] || "";
}

export function finalizeMove(prev, color, nb, capturedType, from, to, type, promo) {
  const nextTurn = color === "white" ? "black" : "white";
  const notation = buildNotation(type, from, to, capturedType, promo, nb, nextTurn, prev.board);
  const resetClock = type === "pawn" || capturedType !== null;
  const nextHalf = resetClock ? 0 : prev.halfmove + 1;
  const pk = posKey(nb, nextTurn);
  const pc = (prev.posHist[pk] || 0) + 1;

  let status = `${nextTurn} to move`;
  if      (isCheckmate(nextTurn, nb))    status = `${color} wins by checkmate`;
  else if (isStalemate(nextTurn, nb))    status = "stalemate";
  else if (insufficientMaterial(nb))     status = "draw by insufficient material";
  else if (nextHalf >= 100)              status = "draw by fifty-move rule";
  else if (pc >= 3)                      status = "draw by threefold repetition";
  else if (inCheck(nextTurn, nb))        status = `${nextTurn} is in check`;

  const history = [...prev.history];
  if (color === "white") history.push({ white: notation, black: "" });
  else if (history.length === 0) history.push({ white: "", black: notation });
  else history[history.length - 1] = { ...history[history.length - 1], black: notation };

  return {
    ...prev,
    board: nb, turn: nextTurn, status,
    selected: null, selectedType: null, legalMoves: [], promo: null, drawOffer: null,
    history, lastMove: { from, to },
    capW: capturedType && color === "black" ? [...prev.capW, capSymbol(capturedType, "white")] : prev.capW,
    capB: capturedType && color === "white" ? [...prev.capB, capSymbol(capturedType, "black")] : prev.capB,
    halfmove: nextHalf,
    posHist: { ...prev.posHist, [pk]: pc },
    botThinking: false,
  };
}
